export interface TransportOrderFormValues {
  // Step 1: Load Details - Locations
  collectionPostcode: string;
  deliveryPostcode: string;

  // Step 1: Load Details - Timing
  collectionDate: string;
  collectionTimeFrom: string;
  collectionTimeTo: string;
  deliveryDate: string;
  deliveryTimeFrom: string;
  deliveryTimeTo: string;
  earliestCollectionTime: string;
  deadlineType: 'time-critical' | 'flexible';
  bookingReference: string;

  // Step 1: Load Details - Carrier and Shipment Method
  carrier: string;
  shipmentMethod: string;

  // Step 1: Load Details - Dimensions and Weight
  totalPallets: number | string;
  palletType: string;
  palletCount: number | string;
  stackable: boolean;
  briefContents: string;
  height: number | string;
  heightUnit: string;
  // Pallet footprint — required in standalone mode when palletType is non-standard
  // (Custom / Other / Unpalletised). Pre-filled for known standards (GB / EU / Half /
  // Mixed) but the user can override. Shares heightUnit.
  palletLength: number | string;
  palletWidth: number | string;
  weight: number | string;
  weightUnit: string;
  overhang: boolean;
  overhangDescription: string;
  packaging: string;

  // Step 1: Load Details - Surcharges. Extras the pallet networks price into the
  // quote when asked for; tailLiftRequired below doubles as the tail-lift surcharge.
  amDeliveryRequired: boolean;
  pmDeliveryRequired: boolean;
  timedDeliveryRequired: boolean;
  saturdayAmDeliveryRequired: boolean;

  preferredVehicle: string;
  curtainSideOrBoxBody: string;
  tailLiftRequired: boolean;
  moffettRequired: boolean;
  minimumInternalHeight: number | string;
  minimumInternalHeightUnit: string;

  // Step 1: Load Details - Loading/Unloading on Site
  forkliftAtCollection: boolean;
  forkliftAtDelivery: boolean;
  dockLoading: boolean;
  handballRequired: boolean;
  palletTruckNeeded: boolean;

  // Step 1: Enhanced Liability Cover
  fragileHandling: boolean;
  insuranceCover: string;
  insuranceCurrency: string;
  sealNumbers: string;

  // Step 2: Collection Address
  pickupCompanyName: string;
  pickupFullAddress: string;
  pickupPostcode: string;
  pickupContactName: string;
  pickupContactNumber: string;
  pickupContactEmail: string;
  pickupAdditionalNotes: string;

  // Step 2: Delivery Address
  dropoffCompanyName: string;
  dropoffFullAddress: string;
  dropoffPostcode: string;
  dropoffContactName: string;
  dropoffContactNumber: string;
  dropoffContactEmail: string;
  dropoffAdditionalNotes: string;

  // Step 3: Selected quote — set when the user clicks a quote card. Required whenever
  // quotes are offered; blank only on the manual-quote path, when none are.
  selectedRateCardId: string;
  selectedQuoteSource: string;
  selectedQuoteServiceName: string;
  selectedQuoteVehicleType: string;
  quotedPrice: number | null;
  quotedCurrency: string;
  quotedAt: string; // ISO timestamp
  quotedRateCardSnapshot: string; // JSON

  // Step 3: Full list of quotes shown to the user (top-5 from the quote engine). Sent on
  // submit as shownQuotes (structured array) for backend audit in TransportOrderCreated. Not
  // validated; mirrors how the selected-quote fields above flow through onSubmit. Empty when no
  // quotes loaded, which leaves the order on the manual-quote path.
  shownQuotes: TransportQuote[];
}

export interface TransportQuote {
  source: string;
  serviceName?: string;
  palletType?: string;
  vehicleType?: string;
  rateCardId: string;
  price?: number;
  currency?: string;
  distanceMiles?: number;
  chargedMiles?: number;
  selectedSurchargeTotal?: number;
  fuelSurchargeTotal?: number;
}

export interface TransportQuotesResponse {
  quotes?: TransportQuote[];
  totalQuotesBeforeShortlist?: number;
}

export const defaultFormValues: TransportOrderFormValues = {
  // Locations
  collectionPostcode: '',
  deliveryPostcode: '',

  // Timing
  collectionDate: '',
  collectionTimeFrom: '09:00',
  collectionTimeTo: '17:00',
  deliveryDate: '',
  deliveryTimeFrom: '09:00',
  deliveryTimeTo: '17:00',
  earliestCollectionTime: '',
  deadlineType: 'time-critical',
  bookingReference: '',

  // Carrier and Shipment Method
  carrier: '',
  shipmentMethod: '',

  // Dimensions and Weight
  totalPallets: '',
  palletType: '',
  palletCount: '',
  stackable: true,
  briefContents: '',
  height: '',
  heightUnit: 'cm',
  palletLength: '',
  palletWidth: '',
  weight: '',
  weightUnit: 'Kg',
  overhang: false,
  overhangDescription: '',
  packaging: '',

  // Surcharges
  amDeliveryRequired: false,
  pmDeliveryRequired: false,
  timedDeliveryRequired: false,
  saturdayAmDeliveryRequired: false,

  preferredVehicle: '',
  curtainSideOrBoxBody: '',
  tailLiftRequired: false,
  moffettRequired: false,
  minimumInternalHeight: '',
  minimumInternalHeightUnit: 'm',

  // Loading/Unloading on Site
  forkliftAtCollection: false,
  forkliftAtDelivery: false,
  dockLoading: false,
  handballRequired: false,
  palletTruckNeeded: false,

  // Step 1: Enhanced Liability Cover
  fragileHandling: false,
  insuranceCover: '',
  insuranceCurrency: 'GBP',
  sealNumbers: '',

  // Step 2: Collection Address
  pickupCompanyName: '',
  pickupFullAddress: '',
  pickupPostcode: '',
  pickupContactName: '',
  pickupContactNumber: '',
  pickupContactEmail: '',
  pickupAdditionalNotes: '',

  // Step 2: Delivery Address
  dropoffCompanyName: '',
  dropoffFullAddress: '',
  dropoffPostcode: '',
  dropoffContactName: '',
  dropoffContactNumber: '',
  dropoffContactEmail: '',
  dropoffAdditionalNotes: '',

  // Step 3: Selected quote (blank until the user picks a card)
  selectedRateCardId: '',
  selectedQuoteSource: '',
  selectedQuoteServiceName: '',
  selectedQuoteVehicleType: '',
  quotedPrice: null,
  quotedCurrency: '',
  quotedAt: '',
  quotedRateCardSnapshot: '',
  shownQuotes: [],
};
